import type { GeoJsonFeatureCollection, MapCloudPoint, MapPointCloud } from '@gpx-spa/geo-domain'
import {
  DEFAULT_POINT_FILL,
  DEFAULT_POINT_RADIUS,
  DEFAULT_POINT_STROKE,
  DEFAULT_POINT_STROKE_W,
} from '../mapbox/mapbox-geojson-style-properties.js'

/** Opaque JSON blob for round-tripping app payload through GeoJSON feature properties. */
export const GPX_POINT_CLOUD_PAYLOAD_KEY = '__gpx_payload'

/** Meters; `0` is stored and means "no ring". */
export const GPX_POINT_CLOUD_ACCURACY_METERS_KEY = '__gpx_accuracy_m'

/** Latitude used by map engines to convert accuracy meters into screen pixels. */
export const GPX_POINT_CLOUD_LATITUDE_KEY = '__gpx_lat'

/** Stable `lat,lng` identity so a clicked point can be found again after thinning. */
export const GPX_POINT_CLOUD_POSITION_KEY = '__gpx_pos'

export function cloudPointPositionKey(position: { lat: number; lng: number }): string {
  return `${position.lat},${position.lng}`
}

export function mapPointCloudToGeoJsonCollection(cloud: MapPointCloud): GeoJsonFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: cloud.points.map((point, pointIndex) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [point.position.lng, point.position.lat],
      },
      properties: {
        ...pointCloudFeatureProperties(point),
        __gpx_point_index: pointIndex,
      },
    })),
  }
}

export function pointCloudFeatureProperties(
  point: MapCloudPoint,
): Record<string, unknown> {
  const style = point.style
  const props: Record<string, unknown> = {
    map: {
      pointFill: style.fillColor,
      pointStroke: style.borderColor ?? DEFAULT_POINT_STROKE,
      pointRadius: style.radius ?? DEFAULT_POINT_RADIUS,
      pointStrokeWidth: style.borderWidth ?? DEFAULT_POINT_STROKE_W,
    },
  }
  if (point.payload && Object.keys(point.payload).length > 0) {
    props[GPX_POINT_CLOUD_PAYLOAD_KEY] = JSON.stringify(point.payload)
  }
  if (
    typeof point.accuracyRadiusMeters === 'number' &&
    Number.isFinite(point.accuracyRadiusMeters)
  ) {
    props[GPX_POINT_CLOUD_ACCURACY_METERS_KEY] = point.accuracyRadiusMeters
  }
  props[GPX_POINT_CLOUD_LATITUDE_KEY] = point.position.lat
  props[GPX_POINT_CLOUD_POSITION_KEY] = cloudPointPositionKey(point.position)
  return props
}

export function mapCloudPointFromGeoJsonFeatureProperties(
  properties: Record<string, unknown> | undefined,
  position: { lat: number; lng: number },
): MapCloudPoint {
  const mapRaw = properties?.map
  const map =
    mapRaw && typeof mapRaw === 'object' && !Array.isArray(mapRaw)
      ? (mapRaw as Record<string, unknown>)
      : {}
  let payload: Record<string, unknown> | undefined
  const rawPayload = properties?.[GPX_POINT_CLOUD_PAYLOAD_KEY]
  if (typeof rawPayload === 'string' && rawPayload.length > 0) {
    try {
      const parsed: unknown = JSON.parse(rawPayload)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        payload = parsed as Record<string, unknown>
      }
    } catch {
      payload = undefined
    }
  }
  const rawAccuracy = properties?.[GPX_POINT_CLOUD_ACCURACY_METERS_KEY]
  const accuracyRadiusMeters =
    typeof rawAccuracy === 'number' && Number.isFinite(rawAccuracy)
      ? rawAccuracy
      : undefined
  return {
    position,
    ...(accuracyRadiusMeters !== undefined ? { accuracyRadiusMeters } : {}),
    style: {
      fillColor:
        typeof map.pointFill === 'string'
          ? map.pointFill
          : typeof properties?.__gpx_pt_fill === 'string'
            ? properties.__gpx_pt_fill
            : DEFAULT_POINT_FILL,
      borderColor:
        typeof map.pointStroke === 'string'
          ? map.pointStroke
          : typeof properties?.__gpx_pt_stroke === 'string'
            ? properties.__gpx_pt_stroke
            : DEFAULT_POINT_STROKE,
      radius:
        typeof map.pointRadius === 'number'
          ? map.pointRadius
          : typeof properties?.__gpx_pt_radius === 'number'
            ? properties.__gpx_pt_radius
            : DEFAULT_POINT_RADIUS,
      borderWidth:
        typeof map.pointStrokeWidth === 'number'
          ? map.pointStrokeWidth
          : typeof properties?.__gpx_pt_stroke_w === 'number'
            ? properties.__gpx_pt_stroke_w
            : DEFAULT_POINT_STROKE_W,
    },
    payload,
  }
}
